import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Monitor } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useStatisticsView, saveChoice } from './seriesApi';
import { normaliseChoice } from './slicing';
import useSlice from './useSlice';
import SeriesChart from './SeriesChart';
import SlicePicker from './SlicePicker';
import SourceLine from './SourceLine';
import { BASE } from './theme';

/**
 * The facilitator's panel for Tilastot seinällä in /admin/workshop: pick the
 * table and the slice, preview it, and send it to the wall
 * (specs/workshop-modules/statistics_view.md). The wall and the iPads reload
 * on the saved choice.
 */

const Wrap = styled.section`
  padding: 24px 32px; display: flex; flex-direction: column; gap: 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  @media (max-width: 640px) { padding: 20px 16px; }
  h3 { margin: 0; font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.75); text-transform: uppercase; letter-spacing: 0.06em; }
  p { margin: 0; line-height: 1.5; }
`;
const Muted = styled.p`color: ${BASE.muted}; font-size: 13px;`;
const ErrorBox = styled.p`
  padding: 8px 12px; border-radius: 8px; font-size: 13px;
  border: 1px solid rgba(255,107,107,0.5); background: rgba(255,107,107,0.12); color: #ffd9d9;
`;
const Preview = styled.div`display: flex; flex-direction: column; min-height: 18rem; max-height: 26rem;`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 10px; align-items: center;`;
const Button = styled.button`
  all: unset; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; min-height: 40px; box-sizing: border-box;
  color: #0b0d12; background: ${BASE.accent}; border: 1px solid ${BASE.accent};
  &:disabled { opacity: 0.45; cursor: default; }
`;

export default function StatisticsViewAdmin({ instanceId }) {
  const { t } = useTranslation();
  const { state, error, reload } = useStatisticsView(instanceId);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const tables = state?.tables || [];
  const wallTable = state?.choice ? tables.find((x) => x.id === state.choice.tableId) : null;
  const table = (draft && tables.find((x) => x.id === draft.tableId)) || wallTable || tables[0] || null;
  const choice = table ? normaliseChoice(table, draft?.tableId === table.id ? draft : state?.choice) : null;
  const { slice } = useSlice(instanceId, table, choice);

  const send = async () => {
    setSaving(true);
    try {
      await saveChoice(instanceId, choice);
      setSaveError(null);
      setDraft(null);
      reload();
    } catch (e) {
      setSaveError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrap>
      <h3>{t('statisticsView.admin.title')}</h3>
      <Muted>{t('statisticsView.admin.lead')}</Muted>
      {error && <ErrorBox>{t('statisticsView.loadError', { message: error.message })}</ErrorBox>}
      {state && !tables.length && <Muted>{t('statisticsView.noTables')}</Muted>}
      {table && choice && (
        <>
          <Muted>{t('statisticsView.admin.dataset', { name: table.dataset_name })} · {t('statisticsView.admin.rows', { count: table.row_count })}</Muted>
          <SlicePicker tables={tables} table={table} choice={choice} onChange={setDraft} />
          <Preview>
            {slice ? <SeriesChart slice={slice} chart={choice.chart} unit={table.unit} missingLabel={t('statisticsView.missing')} /> : <Muted>{t('statisticsView.loading')}</Muted>}
          </Preview>
          <SourceLine table={table} />
          <Actions>
            <Button type="button" onClick={send} disabled={saving}><Monitor size={16} /> {t('statisticsView.showOnWall')}</Button>
            {wallTable && <Muted>{t('statisticsView.shownOnWall', { title: wallTable.title })}</Muted>}
            {saveError && <ErrorBox>{t('statisticsView.saveFailed', { message: saveError.message })}</ErrorBox>}
          </Actions>
        </>
      )}
    </Wrap>
  );
}
